import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Activity, BarChart3, LogOut, MessageSquare, RefreshCw, Users } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { getDashboardStats, type DashboardStats } from "@/lib/analytics.functions";
import { listFeedback, type FeedbackItem } from "@/lib/feedback.functions";
import {
  lastTollRefresh,
  runTollRefreshNow,
  type TollRefreshRun,
} from "@/lib/toll.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/dashboard")({
  head: () => ({
    meta: [
      { title: "Painel de métricas — TrajetoFácil" },
      { name: "description", content: "Acompanhe visitas, usuários ao vivo e rotas mais buscadas no TrajetoFácil." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Painel de métricas — TrajetoFácil" },
      { property: "og:description", content: "Visitas, usuários ao vivo e rotas mais buscadas." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: DashboardPage,
});

function isoDay(d: Date) {
  return d.toISOString().slice(0, 10);
}

function daysAgo(n: number) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - (n - 1));
  return isoDay(d);
}

function DashboardPage() {
  const navigate = useNavigate();
  const fetchStats = useServerFn(getDashboardStats);
  const [preset, setPreset] = useState<7 | 15 | 30 | null>(7);
  const [from, setFrom] = useState(daysAgo(7));
  const [to, setTo] = useState(isoDay(new Date()));

  const query = useQuery<DashboardStats>({
    queryKey: ["dashboard-stats", from, to],
    queryFn: () => fetchStats({ data: { from, to } }),
    refetchInterval: 30_000,
  });

  const stats = query.data;

  const series = useMemo(
    () =>
      (stats?.series ?? [])
        .slice()
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((s) => ({
          ...s,
          label: `${s.date.slice(8, 10)}/${s.date.slice(5, 7)}`,
        })),
    [stats],
  );


  function applyPreset(days: 7 | 15 | 30) {
    setPreset(days);
    setFrom(daysAgo(days));
    setTo(isoDay(new Date()));
  }

  async function signOut() {
    await supabase.auth.signOut();
    await navigate({ to: "/login" });
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-3">
          <span className="flex size-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <BarChart3 className="size-5" />
          </span>
          <div>
            <p className="text-lg font-extrabold leading-none tracking-tight text-foreground">Painel TrajetoFácil</p>
            <p className="text-xs text-muted-foreground">métricas de uso da ferramenta</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => query.refetch()}>
              <RefreshCw className="size-4" /> Atualizar
            </Button>
            <Button variant="ghost" size="sm" onClick={signOut}>
              <LogOut className="size-4" /> Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-6 px-4 py-6">
        <h1 className="sr-only">Painel de métricas do TrajetoFácil</h1>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Usuários ao vivo"
            value={String(stats?.liveUsers ?? 0)}
            hint="ativos nos últimos 5 min"
            live
          />
          <StatCard label="Visitas no período" value={String(stats?.totalVisits ?? 0)} />
          <StatCard label="Visitantes únicos" value={String(stats?.uniqueVisitors ?? 0)} />
          <StatCard label="Buscas de rota" value={String(stats?.totalSearches ?? 0)} />
        </div>

        <section className="rounded-2xl border border-border bg-card p-4 shadow-sm">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="text-sm font-bold uppercase tracking-wider text-foreground">Acessos por dia</h2>
              <p className="text-xs text-muted-foreground">visitas e buscas no período selecionado</p>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              {([7, 15, 30] as const).map((d) => (
                <Button
                  key={d}
                  size="sm"
                  variant={preset === d ? "default" : "outline"}
                  onClick={() => applyPreset(d)}
                >
                  {d} dias
                </Button>
              ))}
              <div className="space-y-1">
                <Label htmlFor="from" className="text-xs text-muted-foreground">
                  De
                </Label>
                <Input
                  id="from"
                  type="date"
                  className="h-9 w-[150px]"
                  value={from}
                  onChange={(e) => {
                    setPreset(null);
                    setFrom(e.target.value);
                  }}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="to" className="text-xs text-muted-foreground">
                  Até
                </Label>
                <Input
                  id="to"
                  type="date"
                  className="h-9 w-[150px]"
                  value={to}
                  onChange={(e) => {
                    setPreset(null);
                    setTo(e.target.value);
                  }}
                />
              </div>
            </div>
          </div>

          <div className="mt-4 h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Line type="monotone" dataKey="visitas" stroke="var(--color-primary)" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="buscas" stroke="var(--color-muted-foreground)" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        <div className="grid gap-4 lg:grid-cols-2">
          <TopChart title="Origens mais acessadas" data={stats?.topOrigins ?? []} />
          <TopChart title="Destinos mais acessados" data={stats?.topDestinations ?? []} />
        </div>

        <TollRefreshCard />
        <FeedbackList />
      </main>
    </div>
  );
}

function StatCard({
  label,
  value,
  hint,
  live,
}: {
  label: string;
  value: string;
  hint?: string;
  live?: boolean;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {live ? <Activity className="size-4 text-primary" /> : <Users className="size-4" />}
        {label}
      </p>
      <p className="mt-2 text-3xl font-extrabold tracking-tight text-foreground">{value}</p>
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function TopChart({ title, data }: { title: string; data: { label: string; count: number }[] }) {
  return (
    <section className="rounded-2xl border border-border bg-card p-4 shadow-sm">
      <h2 className="text-sm font-bold uppercase tracking-wider text-foreground">{title}</h2>
      <div className="mt-3 h-[280px]">
        {data.length === 0 ? (
          <p className="pt-10 text-center text-sm text-muted-foreground">Sem dados no período.</p>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ left: 12, right: 12 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="label" width={110} tick={{ fontSize: 11 }} interval={0} />
              <Tooltip />
              <Bar dataKey="count" fill="var(--color-primary)" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
}

function FeedbackList() {
  const fetchFeedback = useServerFn(listFeedback);
  const { data, isLoading, error } = useQuery<FeedbackItem[]>({
    queryKey: ["feedback-list"],
    queryFn: () => fetchFeedback(undefined as never),
    refetchInterval: 60_000,
  });

  return (
    <section className="rounded-2xl border border-border bg-card p-4 shadow-sm">
      <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-foreground">
        <MessageSquare className="size-4 text-primary" />
        Feedbacks recebidos
      </h2>

      {isLoading ? (
        <p className="mt-4 text-sm text-muted-foreground">Carregando feedbacks…</p>
      ) : error ? (
        <p className="mt-4 text-sm text-destructive">Não foi possível carregar os feedbacks.</p>
      ) : (data?.length ?? 0) === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">Nenhum feedback enviado até agora.</p>
      ) : (
        <ul className="mt-4 space-y-3">
          {data!.map((item) => (
            <li key={item.id} className="rounded-xl border border-border bg-background p-3">
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                <span>{new Date(item.createdAt).toLocaleString("pt-BR")}</span>
                {item.contactEmail ? (
                  <a href={`mailto:${item.contactEmail}`} className="font-semibold text-primary hover:underline">
                    {item.contactEmail}
                  </a>
                ) : (
                  <span>Sem e-mail de contato</span>
                )}
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">{item.message}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function TollRefreshCard() {
  const queryClient = useQueryClient();
  const fetchRun = useServerFn(lastTollRefresh);
  const runNow = useServerFn(runTollRefreshNow);
  const { data } = useQuery<TollRefreshRun | null>({
    queryKey: ["toll-refresh"],
    queryFn: () => fetchRun(undefined as never),
    refetchInterval: 120_000,
  });

  const mutation = useMutation({
    mutationFn: () => runNow(undefined as never),
    onSuccess: (result) => {
      if (result.ok) {
        toast.success(
          `Base atualizada: ${result.withTariff}/${result.plazas} praças com tarifa vigente.`,
        );
      } else if (result.skipped === "locked") {
        toast.info("Já existe uma atualização em andamento. Tente novamente em alguns minutos.");
      } else {
        toast.error(`A atualização falhou: ${result.message ?? "erro desconhecido"}`);
      }
      queryClient.invalidateQueries({ queryKey: ["toll-refresh"] });
    },
    onError: () => {
      toast.error("Não foi possível disparar a atualização agora.");
    },
  });

  const running = mutation.isPending || data?.status === "running";

  return (
    <section className="rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-bold uppercase tracking-wider text-foreground">
            Atualização das tarifas
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Rotina semanal (segundas, 4h UTC) que rebaixa a base oficial de praças e tarifas.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={running}
          onClick={() => mutation.mutate()}
        >
          <RefreshCw className={`size-4 ${running ? "animate-spin" : ""}`} />
          {running ? "Atualizando…" : "Atualizar agora"}
        </Button>
      </div>
      {data ? (
        <dl className="mt-3 space-y-1.5 text-sm">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Última execução</dt>
            <dd className="font-semibold text-foreground">
              {new Date(data.startedAt).toLocaleString("pt-BR")}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Situação</dt>
            <dd className="font-semibold text-foreground">
              {data.paused ? "Pausada" : data.status === "success" ? "OK" : data.status}
            </dd>
          </div>
          {data.message ? (
            <p className="pt-1 text-xs text-muted-foreground">{data.message}</p>
          ) : null}
        </dl>
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">
          Nenhuma execução registrada ainda — a primeira roda no próximo agendamento.
        </p>
      )}
    </section>
  );
}
